import styled from 'styled-components';
import useStore from '@/store/useStore';
import beTwMerge from '@/utils/beTwMerge';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import useMediaQuery from '@/hooks/useMediaQuery';

const generateLabelFromUrl = (url: string): string => {
  if (!url) {
    return '';
  }
  return url
    .split('/')
    .filter(segment => segment.trim() !== '')
    .map(segment => segment.replace(/[^a-zA-Z0-9\s]/g, ' ').trim())
    .join(' ');
};

interface ICtaContainerProps {
  $textColor: string;
  $textColorOnHover: string;
  $fillColor: string;
  $fillColorOnHover: string;
  $outlineColor: string;
  $outlineColorOnHover: string;
}

const CtaContainer = styled.a<ICtaContainerProps>`
  color: ${({ $textColor }) => $textColor};
  background-color: ${({ $fillColor }) => $fillColor};
  outline-color: ${({ $outlineColor }) => $outlineColor};

  &:hover {
    color: ${({ $textColorOnHover }) => $textColorOnHover};
    background-color: ${({ $fillColorOnHover }) => $fillColorOnHover};
    outline-color: ${({ $outlineColorOnHover }) => $outlineColorOnHover};
  }
`;

export default function PromoHeroBanner({ margin, anchorLinkID }: { margin?: boolean; anchorLinkID?: string }) {
  const promo = useStore(state => state.builderData?.promo);
  const desktopPromo = promo?.landingPageHero?.desktop!;
  const mobilePromo = promo?.landingPageHero?.mobile;
  const fullLayoutOption = 'full image';
  const setIsPromoModalVisible = useStore(state => state.setIsPromoModalVisible);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const ariaLabel = generateLabelFromUrl(desktopPromo?.whole_tile_link as unknown as string);

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={beTwMerge(
        'tw-min-h-[600px] min-[1472px]:tw-min-h-[520px] xl:tw-min-h-[500px] lg:tw-min-h-[360px]',
        getComponentMargin(margin)
      )}
      data-ge-hide=""
    >
      {isDesktop ? (
        <a
          href={desktopPromo?.whole_tile_link || ''}
          className="hover:tw-text-transparent active:tw-text-transparent focus:tw-text-transparent"
          aria-label={ariaLabel}
        >
          <section
            style={{ backgroundColor: desktopPromo?.background_hex_color_code }}
            className={beTwMerge(
              'tw-hidden lg:tw-grid tw-mx-4 min-[1472px]:tw-w-full lg:tw-max-w-[1440px] lg:tw-mx-8 min-[1472px]:tw-mx-auto lg:tw-grid-cols-2',
              desktopPromo?.['display_on_page'] ? '' : 'tw-hidden md:tw-hidden lg:tw-hidden'
            )}
          >
            {/* Media */}
            <div
              className={beTwMerge(
                'tw-aspect-[676/512] tw-max-h-[520px] tw-w-full tw-col-span-1',
                desktopPromo?.layout === 'image at right' ? 'tw-order-1' : '-tw-order-1',
                desktopPromo?.layout === fullLayoutOption ? 'tw-col-span-full' : ''
              )}
            >
              <img
                src={desktopPromo?.image.url}
                alt={desktopPromo?.image.alt || ''}
                loading="eager"
                className="tw-w-full tw-max-h-[520px] tw-object-cover tw-self-center tw-my-4 md:tw-my-0"
              />
            </div>
            {/* Text */}
            {desktopPromo?.layout !== fullLayoutOption && (
              <div className="tw-col-span-1 tw-px-12 tw-flex tw-flex-col tw-justify-center tw-h-full">
                {desktopPromo?.['hero_text_list'].map((item, index) => {
                  const { preheader, header, subheader } = item;

                  return (
                    <div
                      key={`${item.header.split(' ').join('')}${index}`}
                      className={beTwMerge(
                        '',
                        index === (desktopPromo?.['hero_text_list'])!.length - 1 ? 'tw-mb-0' : 'tw-mb-6',
                        `tw-${desktopPromo['text_alignment']}`
                      )}
                    >
                      {preheader && (
                        <div
                          className={beTwMerge('tw-mb-2')}
                          style={{
                            fontFamily: desktopPromo?.preheader?.['font-family'],
                            fontSize: desktopPromo?.preheader?.['font-size'],
                            color: desktopPromo?.preheader?.['color'],
                          }}
                          dangerouslySetInnerHTML={{ __html: preheader }}
                        />
                      )}

                      {header && (
                        <h2
                          className="tw-my-0"
                          style={{
                            fontFamily: desktopPromo?.header?.['font-family'],
                            fontSize: desktopPromo?.header?.['font-size'],
                            color: desktopPromo?.header?.['color'],
                            lineHeight: 'inherit',
                          }}
                          dangerouslySetInnerHTML={{ __html: header }}
                        />
                      )}

                      {subheader && (
                        <div
                          className={beTwMerge()}
                          style={{
                            fontFamily: desktopPromo?.subheader?.['font-family'],
                            fontSize: desktopPromo?.subheader?.['font-size'],
                            color: desktopPromo?.subheader?.['color'],
                          }}
                          dangerouslySetInnerHTML={{ __html: subheader }}
                        />
                      )}
                    </div>
                  );
                })}

                {desktopPromo?.cta?.text && (
                  <div
                    className={beTwMerge(
                      'tw-mt-6 tw-flex',
                      // eslint-disable-next-line no-nested-ternary
                      desktopPromo?.['text_alignment'] === 'text-center'
                        ? 'tw-justify-center'
                        : desktopPromo?.['text_alignment'] === 'text-left'
                          ? 'tw-justify-start'
                          : 'tw-justify-end'
                    )}
                  >
                    <CtaContainer
                      $textColor={desktopPromo?.cta?.['text_color']}
                      $fillColor={desktopPromo?.cta?.['cta_fill_color']}
                      $outlineColor={desktopPromo?.cta?.['outline_color']}
                      $textColorOnHover={desktopPromo?.cta?.['text_color_on_hover']}
                      $fillColorOnHover={desktopPromo?.cta?.['fill_color_on_hover']}
                      $outlineColorOnHover={desktopPromo?.cta?.['outline_color_on_hover']}
                      href={desktopPromo?.cta?.['link'] || ''}
                      className="hover:tw-no-underline tw-no-underline tw-py-3 tw-w-full tw-max-w-[294px] tw-block tw-text-center tw-border tw-border-solid"
                    >
                      {desktopPromo?.cta?.text}
                    </CtaContainer>
                  </div>
                )}
              </div>
            )}
          </section>
        </a>
      ) : (
        <a
          href={mobilePromo?.whole_tile_link || ''}
          className="hover:tw-text-transparent active:tw-text-transparent focus:tw-text-transparent"
          aria-label={ariaLabel}
        >
          <section
            style={{ backgroundColor: desktopPromo?.['background_hex_color_code'] }}
            className={beTwMerge(
              'tw-grid tw-mx-4 lg:tw-w-full lg:tw-max-w-[1440px] lg:tw-mx-8 min-[1472px]:tw-mx-auto lg:tw-grid-cols-2',
              mobilePromo?.['display_on_page'] ? 'lg:tw-hidden' : 'sm:tw-hidden md:tw-hidden'
            )}
          >
            {/* Media */}
            <div
              className={beTwMerge(
                'lg:tw-aspect-[676/512] tw-w-full tw-col-span-1',
                mobilePromo?.layout === 'image at right' ? 'tw-order-1' : '-tw-order-1'
              )}
            >
              <img
                src={mobilePromo?.image.url}
                alt={mobilePromo?.image.alt || ''}
                loading="eager"
                className="tw-w-full tw-self-center md:tw-my-0"
              />
            </div>
            {/* Text */}
            {desktopPromo?.layout !== fullLayoutOption && (
              <div className="tw-col-span-1 tw-px-12 tw-flex tw-flex-col tw-justify-center tw-h-full tw-pt-4 tw-pb-7">
                {mobilePromo?.['hero_text_list'].map((item, index) => {
                  const { preheader, header, subheader } = item;

                  return (
                    <div
                      key={`${item.header.split(' ').join('')}${index}`}
                      className={beTwMerge(
                        '',
                        index === (desktopPromo?.['hero_text_list'])!.length - 1 ? 'tw-mb-0' : 'tw-mb-6',
                        `tw-${mobilePromo['text_alignment']}`
                      )}
                    >
                      {preheader && (
                        <div
                          className={beTwMerge('tw-mb-2')}
                          style={{
                            fontFamily: mobilePromo?.preheader?.['font-family'],
                            fontSize: mobilePromo?.preheader?.['font-size'],
                            color: mobilePromo?.preheader?.['color'],
                          }}
                          dangerouslySetInnerHTML={{ __html: preheader }}
                        />
                      )}

                      {header && (
                        <h2
                          className="tw-my-0"
                          style={{
                            fontFamily: mobilePromo?.header?.['font-family'],
                            fontSize: mobilePromo?.header?.['font-size'],
                            color: mobilePromo?.header?.['color'],
                            lineHeight: 'inherit',
                          }}
                          dangerouslySetInnerHTML={{ __html: header }}
                        />
                      )}

                      {subheader && (
                        <div
                          className={beTwMerge()}
                          style={{
                            fontFamily: mobilePromo?.subheader?.['font-family'],
                            fontSize: mobilePromo?.subheader?.['font-size'],
                            color: mobilePromo?.subheader?.['color'],
                          }}
                          dangerouslySetInnerHTML={{ __html: subheader }}
                        />
                      )}
                    </div>
                  );
                })}

                {mobilePromo?.cta?.text && (
                  <div
                    className={beTwMerge(
                      'tw-mt-6 tw-flex',
                      // eslint-disable-next-line no-nested-ternary
                      mobilePromo?.['text_alignment'] === 'text-center'
                        ? 'tw-justify-center'
                        : mobilePromo?.['text_alignment'] === 'text-left'
                          ? 'tw-justify-start'
                          : 'tw-justify-end'
                    )}
                  >
                    <CtaContainer
                      $textColor={mobilePromo?.cta?.['text_color']}
                      $fillColor={mobilePromo?.cta?.['cta_fill_color']}
                      $outlineColor={mobilePromo?.cta?.['outline_color']}
                      $textColorOnHover={mobilePromo?.cta?.['text_color_on_hover']}
                      $fillColorOnHover={mobilePromo?.cta?.['fill_color_on_hover']}
                      $outlineColorOnHover={mobilePromo?.cta?.['outline_color_on_hover']}
                      href={mobilePromo?.cta?.['link'] || ''}
                      className="hover:tw-no-underline tw-no-underline tw-py-3 tw-w-full tw-max-w-[294px] tw-block tw-text-center tw-border tw-border-solid"
                    >
                      {mobilePromo?.cta?.text}
                    </CtaContainer>
                  </div>
                )}
              </div>
            )}
          </section>
        </a>
      )}

      {desktopPromo?.['t_c'] && (
        <div
          className={beTwMerge(
            'tw-hidden lg:tw-block tw-mx-4 lg:tw-w-full lg:tw-max-w-[1440px] lg:tw-mx-8 min-[1472px]:tw-mx-auto tw-mt-6',
            desktopPromo?.layout === fullLayoutOption && 'tw-mt-6'
          )}
        >
          <a
            type="button"
            onClick={() => setIsPromoModalVisible(true)}
            style={{
              fontFamily: desktopPromo?.['t_c']?.['font-family'],
              fontSize: desktopPromo?.['t_c']?.['font-size'],
              color: desktopPromo?.['t_c']?.['color'],
              cursor: 'pointer',
            }}
            className={beTwMerge(
              'focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]',
              true && '[&>u]:tw-text-be-base hover:[&>u]:tw-text-[#183E40]'
            )}
            dangerouslySetInnerHTML={{ __html: desktopPromo?.['t_c']?.text || '' }}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setIsPromoModalVisible(true);
              }
            }}
          />

          {desktopPromo?.cta?.text && desktopPromo?.layout === fullLayoutOption && (
            <div className="tw-mt-4">
              <CtaContainer
                $textColor={desktopPromo?.cta?.['text_color']}
                $fillColor={desktopPromo?.cta?.['cta_fill_color']}
                $outlineColor={desktopPromo?.cta?.['outline_color']}
                $textColorOnHover={desktopPromo?.cta?.['text_color_on_hover']}
                $fillColorOnHover={desktopPromo?.cta?.['fill_color_on_hover']}
                $outlineColorOnHover={desktopPromo?.cta?.['outline_color_on_hover']}
                href={desktopPromo?.cta?.['link'] || ''}
                className="hover:tw-no-underline tw-no-underline tw-py-3 tw-w-full tw-max-w-[294px] tw-block tw-text-center tw-border tw-border-solid"
              >
                {desktopPromo?.cta?.text}
              </CtaContainer>
            </div>
          )}
        </div>
      )}

      {mobilePromo?.['t_c'] && (
        <div
          className={beTwMerge(
            'tw-flex tw-justify-center lg:tw-hidden tw-mx-4 lg:tw-w-full lg:tw-max-w-[1440px] lg:tw-mx-8 min-[1472px]:tw-mx-auto tw-mt-6',
            desktopPromo?.layout === fullLayoutOption && 'tw-flex tw-items-center tw-flex-col tw-mt-6'
          )}
        >
          <a
            type="button"
            onClick={() => setIsPromoModalVisible(true)}
            style={{
              fontFamily: mobilePromo?.['t_c']?.['font-family'],
              fontSize: mobilePromo?.['t_c']?.['font-size'],
              color: mobilePromo?.['t_c']?.['color'],
              cursor: 'pointer',
            }}
            className={beTwMerge(
              'focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]',
              true && '[&>u]:tw-text-be-base'
            )}
            dangerouslySetInnerHTML={{ __html: mobilePromo?.['t_c']?.text || '' }}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setIsPromoModalVisible(true);
              }
            }}
          />

          {mobilePromo?.cta?.text && desktopPromo?.layout === fullLayoutOption && (
            <div className="tw-mt-4 tw-w-full tw-flex tw-justify-center">
              <CtaContainer
                $textColor={mobilePromo?.cta?.['text_color']}
                $fillColor={mobilePromo?.cta?.['cta_fill_color']}
                $outlineColor={mobilePromo?.cta?.['outline_color']}
                $textColorOnHover={mobilePromo?.cta?.['text_color_on_hover']}
                $fillColorOnHover={mobilePromo?.cta?.['fill_color_on_hover']}
                $outlineColorOnHover={mobilePromo?.cta?.['outline_color_on_hover']}
                href={mobilePromo?.cta?.['link'] || ''}
                className="hover:tw-no-underline tw-no-underline tw-py-3 tw-w-full tw-text-center tw-border tw-border-solid tw-block md:tw-max-w-[480px]"
              >
                {mobilePromo?.cta?.text}
              </CtaContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
